import React, { useContext, useEffect, useRef, useState } from "react";
import { FormContext } from "../form_buttons";
import { Autocomplete, TextField } from "@mui/material";
import { TypeConf, useType } from "../../types";
import { Box } from "@mui/system";
import { fieldMarginBottom } from "../../form_constants";

interface FormAutocompleteMultiProp<T> {
    field: string;
    label: string;
    type: TypeConf<T>;
    helpText?: string;
    readOnly?: boolean;
    disabled?: boolean;
    onChange?: (type: TypeConf<T>, value: T[]) => void;
}

function FormAutocompleteMulti<T>(props: FormAutocompleteMultiProp<T>) {
    const { field, label, type, helpText, onChange, readOnly, disabled } = props;

    const typer = useType(type, true);

    const [elements, setElements] = useState<T[]>([]);
    const [selectedItems, setSelectedItems] = useState<T[]>([]);
    const [userInput, setUserInput] = useState<string>("");

    // Versión para descartar respuestas obsoletas de peticiones anteriores
    const requestVersionRef = useRef(0);

    const formContext = useContext(FormContext);
    const model = formContext.form;
    const modelField = model.field(field);

    // Un enum viaja como texto (["QUALITY_COORDINATOR"]), no como el objeto del desplegable (#13911).
    const toItem = (v: any): T => type.isEnum && typeof v === "string" ? ({value: v, label: v} as unknown as T) : v;
    const toValue = (items: T[]): any[] => type.isEnum ? items.map(i => type.valuer(i)) : items;

    model.register(
        field,
        () => toValue(selectedItems),
        incoming => {
            if (!incoming) {
                setSelectedItems([]);
            } else if (Array.isArray(incoming)) {
                setSelectedItems(incoming.map(toItem));
            } else {
                setSelectedItems([toItem(incoming)]);
            }
        },
    );

    const valuer = (item: T | undefined | null): string =>
        item ? `${type.valuer(item) ?? ""}` : "";

    const labeler = (item: T | undefined | null): string =>
        item ? `${type.labeler(item) ?? ""}` : "";

    useEffect(() => {
        const current = modelField.value();
        if (Array.isArray(current)) setSelectedItems(current.map(toItem));
    }, []);

    useEffect(() => {
        const version = ++requestVersionRef.current;
        typer.supplier(userInput).then(array => {
            if (version === requestVersionRef.current) setElements(array);
        });
    }, [userInput]);

    const handleChange = (_: React.SyntheticEvent, newValue: T[]) => {
        setSelectedItems(newValue);
        model.notifyFieldChange(field, toValue(newValue));
        onChange?.(type, newValue);
    };

    return (
        <Box className={["form-input", "form-input-" + field].join(" ")} mb={fieldMarginBottom}>
            <Autocomplete
                multiple
                fullWidth
                id={field}
                value={selectedItems}
                options={elements}
                disablePortal
                readOnly={readOnly}
                disabled={disabled}
                filterOptions={x => x}
                isOptionEqualToValue={(option, val) => valuer(option) === valuer(val)}
                getOptionLabel={option => labeler(option)}
                onChange={handleChange}
                onInputChange={(_e, val) => setUserInput(val)}
                renderOption={(optProps, option) => (
                    <li {...optProps} key={valuer(option)}>
                        {labeler(option)}
                    </li>
                )}
                renderInput={params => (
                    <TextField {...params} label={label} fullWidth helperText={helpText} />
                )}
            />
        </Box>
    );
}

export default FormAutocompleteMulti;
